<template>
  <div>
    <!-- Button -->
    <v-btn
      :small="$vuetify.breakpoint.xsOnly"
      color="primary"
      class="mx-0"
      @click="filtersSidebar = !filtersSidebar"
    >
      Filters
      <v-icon
        v-if="!$vuetify.breakpoint.xsOnly"
        right
      >
        fa-filter
      </v-icon>
    </v-btn>

    <!-- Drawer -->
    <v-navigation-drawer
      v-model="filtersSidebar"
      :width="415"
      app
      right
      temporary
      class="grey lighten-3"
    >
      <v-toolbar
        dark
        text
      >
        <v-btn
          v-if="$vuetify.breakpoint.xsOnly"
          icon
          class="ma-0"
          @click="filtersSidebar = false"
        >
          <v-icon>close</v-icon>
        </v-btn>
        <v-toolbar-title>Filters ({{ totalFiltersUsed }})</v-toolbar-title>
        <v-spacer />
        <v-btn
          color="error"
          class="pa-0 my-0"
          @click="reset()"
        >
          Reset
        </v-btn>
      </v-toolbar>

      <v-expansion-panels accordion>
        <v-expansion-panel
          v-for="filter in filtersProxy"
          :key="filter.title"
        >
          <v-expansion-panel-header>
            <span
              v-if="filter.isUsed"
              class="mr-2 accent--text shrink"
            >&#9679;</span>
            <span class="text-body-2">{{ filter.title }}</span>
          </v-expansion-panel-header>
          <v-expansion-panel-content class="grey lighten-5">
            <!-- KEYWORD -->
            <template v-if="filter.type === 'keyword'">
              <v-form
                v-model="filter.formValid"
                @submit.prevent
              >
                <v-row align="center">
                  <v-col
                    v-if="filter.options"
                    cols="12"
                  >
                    <v-select
                      v-model="filter.selectedOption"
                      :items="filter.options"
                      label="Keyword search by"
                      menu-props="offsetY"
                    />
                  </v-col>
                  <v-col cols="9">
                    <v-text-field
                      v-model.trim="filter.value"
                      :label="`Enter ${filter.title}`"
                      :rules="[v => !v || v.length <= 50 || 'Keywords must be less than 50 characters']"
                      :counter="50"
                      prepend-inner-icon="search"
                      clearable
                      @keyup.enter="filter.formValid && genParamsObject()"
                    />
                  </v-col>
                  <v-col cols="3">
                    <v-btn
                      :disabled="!filter.formValid"
                      depressed
                      color="primary"
                      @click="filter.formValid && genParamsObject()"
                    >
                      Search
                    </v-btn>
                  </v-col>
                </v-row>
              </v-form>
            </template>

            <!-- TEXT -->
            <template v-else-if="filter.type === 'text'">
              <v-row align="center">
                <v-col cols="9">
                  <v-text-field
                    v-model.trim="filter.value"
                    :label="`Enter ${filter.title}`"
                    :counter="50"
                    :error-messages="filter.error || null"
                    prepend-inner-icon="add"
                    clearable
                    @input="resetErrorState(filter)"
                    @keyup.enter="addCustomText(filter)"
                  />
                </v-col>
                <v-col cols="3">
                  <v-btn
                    depressed
                    color="primary"
                    @click="addCustomText(filter)"
                  >
                    Add
                  </v-btn>
                </v-col>
              </v-row>
              <div
                v-if="filter.items"
                class="mt-2"
              >
                <transition-group name="slide-y-reverse-transition">
                  <v-chip
                    v-for="item in filter.items"
                    :key="item.id"
                    label
                    close
                    @update:active="removeCustomText(filter.items, item)"
                  >
                    {{ item.description }}
                  </v-chip>
                </transition-group>
              </div>
            </template>

            <!-- NUMBER RANGE -->
            <template v-else-if="filter.type === 'number_range'">
              <v-row no-gutters>
                <div
                  v-if="filter.help"
                  class="d-flex mb-6"
                >
                  <v-icon
                    outlined
                    color="info"
                    class="mr-2"
                  >
                    info_outline
                  </v-icon>
                  <div class="font-weight-light">
                    {{ filter.help }}
                  </div>
                </div>
                <v-col
                  v-for="item in filter.items"
                  :key="item.id"
                  cols="12"
                >
                  <v-row
                    no-gutters
                    justify="space-between"
                    align="center"
                  >
                    <v-col>
                      <v-switch
                        v-model="item.selected"
                        :label="item.description"
                        title="Click to toggle filter"
                        color="primary"
                        hide-details
                        class="mt-0"
                        @change="genParamsObject()"
                      />
                    </v-col>
                    <v-col
                      v-if="item.count"
                      class="shrink"
                      text-right
                    >
                      <v-chip
                        small
                        disabled
                        class="ma-0"
                      >
                        {{ item.count }}
                      </v-chip>
                    </v-col>
                  </v-row>
                </v-col>
                <v-subheader
                  v-if="filter.items"
                  class="mt-2 pl-0"
                >
                  Custom range:
                </v-subheader>
                <v-col cols="12">
                  <v-container
                    pa-0
                    fluid
                    grid-list-md
                  >
                    <v-row align="end">
                      <v-col cols="4">
                        <v-text-field
                          v-model.number="filter.from"
                          :error="Boolean(filter.error)"
                          :prefix="filter.prefix"
                          :suffix="filter.suffix"
                          type="number"
                          label="FROM"
                          placeholder=" "
                          hide-details
                          @input="resetErrorState(filter)"
                          @keyup.enter="addCustomNumberRange(filter)"
                        />
                      </v-col>
                      <v-col cols="4">
                        <v-text-field
                          v-model.number="filter.to"
                          :error="Boolean(filter.error)"
                          :prefix="filter.prefix"
                          :suffix="filter.suffix"
                          type="number"
                          label="TO"
                          placeholder=" "
                          hide-details
                          @input="resetErrorState(filter)"
                          @keyup.enter="addCustomNumberRange(filter)"
                        />
                      </v-col>
                      <v-col cols="4">
                        <v-btn
                          text
                          color="success"
                          title="Add custom range"
                          class="ma-0"
                          style="min-width: 0"
                          @click="addCustomNumberRange(filter)"
                        >
                          Add
                        </v-btn>
                      </v-col>
                      <v-col
                        v-show="filter.error"
                        cols="12"
                        class="v-messages error--text"
                      >
                        {{ filter.error }}
                      </v-col>
                    </v-row>
                  </v-container>
                </v-col>
              </v-row>
            </template>
            <template v-else-if="filter.type === 'mc_concentration_type'">
              <v-switch
                v-model="filter.selected"
                label="Enable filtering by Market Cap"
                hide-details
                @click="updateMcConcentration(filter)"
              />
              <v-row dense>
                <v-col cols="12">
                  <v-range-slider
                    v-model="filter.market_cap_range"
                    label="Set Market Cap Range"
                    :disabled="!filter.selected"
                    :tick-labels="filter.labels"
                    :value="[1,2,3,4,5,6,7,8,9,10]"
                    min="1"
                    max="10"
                    vertical
                    ticks="always"
                    @change="updateMcConcentration(filter)"
                  />
                </v-col>
                <v-col cols="6">
                  <v-text-field
                    v-model="filter.from"
                    :disabled="!filter.selected"
                    label="% of portfolio"
                    persistent-hint
                    hint="Entering the lower range for the % of portfolio that is invested in the market cap range selected above"
                    style="max-width: 200px"
                    @change="updateMcConcentration(filter)"
                  />
                </v-col>
                <v-col cols="6">
                  <v-text-field
                    v-model="filter.to"
                    :disabled="!filter.selected"
                    label="To"
                    persistent-hint
                    hint="Entering the upper range for the % of portfolio that is invested in the market cap range selected above"
                    style="max-width: 200px"
                    @change="updateMcConcentration(filter)"
                  />
                </v-col>
              </v-row>
            </template>
            <!-- DYNAMIC NUMBER RANGE -->
            <template v-else-if="filter.type === 'dynamic_number_range'">
              <v-row no-gutters>
                <v-col
                  v-for="item in filter.items"
                  :key="item.id"
                  cols="12"
                >
                  <v-switch
                    v-model="item.selected"
                    :label="item.name"
                    color="primary"
                    hide-details
                    title="Click to toggle filter"
                    class="mt-0"
                    @change="isEnabled => !isEnabled && resetDynamicNumberRange(item)"
                  />
                  <v-slide-y-transition>
                    <v-container
                      v-if="item.selected"
                      pa-0
                      fluid
                      grid-list-md
                    >
                      <v-row
                        align="end"
                        class="my-2"
                      >
                        <v-col cols="4">
                          <v-text-field
                            v-model.number="item.from"
                            :error="Boolean(item.error)"
                            :prefix="filter.prefix"
                            :suffix="filter.suffix"
                            type="number"
                            label="FROM"
                            placeholder=" "
                            hide-details
                            @input="resetErrorState(item)"
                            @keyup.enter="updateDynamicNumberRange(filter, item)"
                          />
                        </v-col>
                        <v-col cols="4">
                          <v-text-field
                            v-model.number="item.to"
                            :error="Boolean(item.error)"
                            :prefix="filter.prefix"
                            :suffix="filter.suffix"
                            type="number"
                            label="TO"
                            placeholder=" "
                            hide-details
                            @input="resetErrorState(item)"
                            @keyup.enter="updateDynamicNumberRange(filter, item)"
                          />
                        </v-col>
                        <v-col cols="4">
                          <v-btn
                            text
                            color="success"
                            title="Add custom range"
                            class="ma-0"
                            style="min-width: 0"
                            @click="updateDynamicNumberRange(filter, item)"
                          >
                            Update
                          </v-btn>
                        </v-col>
                        <v-col
                          v-if="item.error"
                          cols="12"
                          class="v-messages error--text"
                        >
                          {{ item.error }}
                        </v-col>
                      </v-row>
                    </v-container>
                  </v-slide-y-transition>
                </v-col>
              </v-row>
            </template>

            <!--DATE RANGE -->
            <template v-else-if="filter.type === 'date_range'">
              <v-row no-gutters>
                <v-col
                  v-for="item in filter.items"
                  :key="item.id"
                  cols="12"
                >
                  <v-row
                    no-gutters
                    justify="space-between"
                    align="center"
                  >
                    <v-col>
                      <v-switch
                        v-model="item.selected"
                        :label="item.description"
                        title="Click to toggle filter"
                        color="primary"
                        hide-details
                        class="mt-0"
                        @change="genParamsObject()"
                      />
                    </v-col>
                    <v-col
                      v-if="item.count"
                      class="shrink"
                      text-right
                    >
                      <v-chip
                        small
                        disabled
                        class="ma-0"
                      >
                        {{ item.count }}
                      </v-chip>
                    </v-col>
                  </v-row>
                </v-col>
                <v-subheader
                  v-if="filter.items"
                  class="mt-2 pl-0"
                >
                  Custom range:
                </v-subheader>
                <v-col cols="12">
                  <v-container
                    pa-0
                    fluid
                    grid-list-md
                  >
                    <v-row
                      no-gutters
                      align="end"
                    >
                      <v-col cols="5">
                        <v-menu
                          :close-on-content-click="false"

                          transition="scale-transition"
                          offset-y
                          min-width="290px"
                        >
                          <template v-slot:activator="{ on }">
                            <v-text-field
                              v-model="filter.from"
                              :error="Boolean(filter.error)"
                              label="Starting date"
                              prepend-icon="event"
                              hide-details
                              readonly
                              v-on="on"
                            />
                          </template>
                          <v-date-picker
                            v-model="filter.from"
                            no-title
                            scrollable
                            @input="resetErrorState(filter)"
                          />
                        </v-menu>
                      </v-col>
                      <v-col cols="5">
                        <v-menu
                          :close-on-content-click="false"

                          transition="scale-transition"
                          offset-y

                          min-width="290px"
                        >
                          <template v-slot:activator="{ on }">
                            <v-text-field
                              v-model="filter.to"
                              :error="Boolean(filter.error)"
                              label="Ending date"
                              prepend-icon="event"
                              hide-details
                              readonly
                              v-on="on"
                            />
                          </template>
                          <v-date-picker
                            v-model="filter.to"
                            no-title
                            scrollable
                            @input="resetErrorState(filter)"
                          />
                        </v-menu>
                      </v-col>
                      <v-col cols="2">
                        <v-btn
                          text
                          color="success"
                          title="Add custom range"
                          class="ma-0"
                          style="min-width: 0"
                          @click="addCustomDateRange(filter)"
                        >
                          Add
                        </v-btn>
                      </v-col>
                      <v-col
                        v-show="filter.error"
                        cols="12"
                        class="v-messages error--text"
                      >
                        {{ filter.error }}
                      </v-col>
                    </v-row>
                  </v-container>
                </v-col>
              </v-row>
            </template>

            <!-- QUARTER RANGE -->
            <template v-else-if="filter.type === 'quarter_range'">
              <v-row no-gutters>
                <v-col
                  v-for="item in filter.items"
                  :key="item.id"
                  cols="12"
                >
                  <v-row
                    justify="space-between"
                    align="center"
                  >
                    <v-col>
                      <v-switch
                        v-model="item.selected"
                        :label="item.description"
                        color="primary"
                        title="Click to toggle filter"
                        hide-details
                        class="mt-0"
                        @change="genParamsObject()"
                      />
                    </v-col>
                    <v-col
                      v-if="item.count"
                      class="shrink"
                      text-right
                    >
                      <v-chip
                        small
                        disabled
                        class="ma-0"
                      >
                        {{ item.count }}
                      </v-chip>
                    </v-col>
                  </v-row>
                </v-col>
                <v-col
                  :mt-3="filter.items"
                  cols="12"
                >
                  <v-container
                    pa-0
                    fluid
                    grid-list-md
                  >
                    <v-row align="end">
                      <v-col cols="4">
                        <v-select
                          v-model="filter.from"
                          :items="filter.quarters"
                          :error="Boolean(filter.error)"
                          label="From"
                          item-text="description"
                          return-object
                          hide-details
                          @input="resetErrorState(filter)"
                        />
                      </v-col>
                      <v-col cols="4">
                        <v-select
                          v-model="filter.to"
                          :items="filter.quarters"
                          :error="Boolean(filter.error)"
                          label="Until"
                          item-text="description"
                          return-object
                          hide-details
                          @input="resetErrorState(filter)"
                        />
                      </v-col>
                      <v-col cols="3">
                        <v-btn
                          text
                          color="success"
                          title="Add quarter range"
                          class="ma-0"
                          style="min-width: 0"
                          @click="addQuarterRange(filter)"
                        >
                          Add
                        </v-btn>
                      </v-col>
                      <v-col
                        v-if="filter.error"
                        cols="12"
                        class="v-messages error--text"
                      >
                        {{ filter.error }}
                      </v-col>
                    </v-row>
                  </v-container>
                </v-col>
              </v-row>
            </template>

            <!-- PROXIMITY -->
            <template v-else-if="filter.type === 'proximity'">
              <v-row no-gutters>
                <v-col cols="12">
                  <v-text-field
                    v-model.trim="filter.postalCode"
                    label="Postal code or address"
                    @input="resetErrorState(filter)"
                  />
                </v-col>
                <v-col cols="12">
                  <v-select
                    v-model="filter.miles"
                    :items="proximityRange"
                    label="Distance in miles"
                    menu-props="offsetY"
                    @input="resetErrorState(filter)"
                  />
                </v-col>
                <v-col cols="12">
                  <v-btn
                    color="primary"
                    class="mx-0"
                    @click="updateProximity(filter)"
                  >
                    Search
                  </v-btn>
                  <v-btn
                    text
                    color="error"
                    @click="clearProximity(filter)"
                  >
                    Clear
                  </v-btn>
                </v-col>
                <v-col
                  v-show="filter.error"
                  cols="12"
                  class="v-messages error--text"
                >
                  {{ filter.error }}
                </v-col>
              </v-row>
            </template>
            <!-- ELSE: V-Radio -->
            <template v-else-if="filter.type === 'select-one'">
              <v-row no-gutters>
                <div
                  v-if="filter.help"
                  class="d-flex mb-6"
                >
                  <v-icon
                    outlined
                    color="info"
                    class="mr-2"
                  >
                    info_outline
                  </v-icon>
                  <div class="font-weight-light">
                    {{ filter.help }}
                  </div>
                </div>
                <v-col
                  cols="12"
                >
                  <v-radio-group v-model="filter.value" @change="genParamsObject()">
                    <v-row
                      v-for="item in filter.items"
                      :key="item.id"
                      no-gutters
                      justify="space-between"
                      align="center"
                    >
                      <v-col>
                        <v-radio

                          :label="item.description"
                          :value="item.id"
                          hide-details
                        />
                      </v-col>
                      <v-col
                        v-if="item.count"
                        class="shrink"
                        text-right
                      >
                        <v-chip
                          small
                          disabled
                          class="ma-0"
                        >
                          {{ item.count }}
                        </v-chip>
                      </v-col>
                    </v-row>
                  </v-radio-group>
                </v-col>
              </v-row>
            </template>
            <!-- ELSE: V-SWITCH -->
            <template v-else>
              <v-row no-gutters>
                <div
                  v-if="filter.help"
                  class="d-flex mb-6"
                >
                  <v-icon
                    outlined
                    color="info"
                    class="mr-2"
                  >
                    info_outline
                  </v-icon>
                  <div class="font-weight-light">
                    {{ filter.help }}
                  </div>
                </div>
                <v-col
                  v-for="item in filter.items"
                  :key="item.id"
                  cols="12"
                >
                  <v-row
                    no-gutters
                    justify="space-between"
                    align="center"
                  >
                    <v-col>
                      <v-switch
                        v-model="item.selected"
                        :label="item.description"
                        color="primary"
                        title="Click to toggle filter"
                        class="mt-0"
                        hide-details
                        @change="genParamsObject()"
                      />
                    </v-col>
                    <v-col
                      v-if="item.count"
                      class="shrink"
                      text-right
                    >
                      <v-chip
                        small
                        disabled
                        class="ma-0"
                      >
                        {{ item.count }}
                      </v-chip>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
            </template>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>
    </v-navigation-drawer>
  </div>
</template>

<script>
// Modules
import debounce from 'lodash/debounce'
import cloneDeep from 'lodash/cloneDeep'

export default {
  props: {
    filters: {
      type: Array,
      required: true
    },
    filtersCounts: {
      type: Object,
      required: true
    },
    paramsObject: {
      type: Object,
      required: true
    }
  },

  data () {
    return {
      filtersSidebar: false,
      filtersProxy: [],
      proximityRange: [10, 25, 100, 250]
    }
  },

  computed: {
    totalFiltersUsed () {
      return this.filtersProxy.filter(f => f.isUsed).length
    }
  },

  watch: {
    filtersCounts: {
      deep: true,
      handler () {
        this.syncFiltersCounts()
      }
    }
  },

  created () {
    this.filtersProxy = cloneDeep(this.filters)
  },

  methods: {
    genParamsObject: debounce(function () {
      const params = {}

      this.filtersProxy.forEach(filter => {
        // Keyword
        if (filter.type === 'keyword') {
          const key = filter.identifier
          if (!filter.value) {
            delete params[key]
            if (filter.options) delete params.text_to_search
          } else {
            params[key] = filter.value
            if (filter.options) params.text_to_search = filter.selectedOption
          }
        }

        // Text
        else if (filter.type === 'text') {
          filter.items && filter.items
            .forEach(item => {
              const key = `${filter.identifier}_from_to`
              if (!params[key]) params[key] = []
              params[key].push(item.id)
            })
        }

        // Number range
        else if (filter.type === 'number_range') {
          filter.items && filter.items
            .filter(item => item.selected)
            .forEach(selectedItem => {
              const key = selectedItem.custom ? `${filter.identifier}_from_to` : filter.identifier
              if (!params[key]) params[key] = []
              params[key].push(selectedItem.id)
            })
        }
        else if (filter.type === 'mc_concentration_type') {
          var valid = (filter.selected && filter.market_cap_range.length > 0 &&
                (filter.from && filter.from.trim().length > 0) && (filter.to && filter.to.trim().length > 0))
          if (valid) {
            const key = `${filter.identifier}_from_to`
            if (!params[key]) params[key] = []
            params[key].push(filter.value)
          }
        }
        // Dynamic number range
        else if (filter.type === 'dynamic_number_range') {
          filter.items && filter.items
            .filter(item => item.selected)
            .forEach(selectedItem => {
              if (!selectedItem.from || !selectedItem.to) return

              const key = `${filter.identifier}_from_to`
              if (!params[key]) params[key] = []
              params[key].push(selectedItem.value)
            })
        }

        // Quarter range
        else if (filter.type === 'quarter_range') {
          filter.items && filter.items
            .filter(item => item.selected)
            .forEach(selectedItem => {
              const key = `${filter.identifier}_from_to`
              if (!params[key]) params[key] = []
              params[key].push(selectedItem.id)
            })
        }

        // Date range
        else if (filter.type === 'date_range') {
          filter.items && filter.items
            .filter(item => item.selected)
            .forEach(selectedItem => {
              const key = selectedItem.custom ? `${filter.identifier}_from_to` : filter.identifier
              if (!params[key]) params[key] = []
              params[key].push(selectedItem.id)
            })
        }

        // Proximity
        else if (filter.type === 'proximity') {
          params.proximity_miles = filter.miles
          params.proximity_postal_code = filter.postalCode
        }
        else if (filter.type === 'select-one') {
          const key = filter.identifier
          if (!filter.value) {
            delete params[key]
          } else {
            params[key] = filter.value
          }
        }

        // else: Switches
        else {
          filter.items && filter.items
            .filter(item => item.selected)
            .forEach(selectedItem => {
              const key = filter.identifier
              if (!params[key]) params[key] = []
              params[key].push(selectedItem.id)
            })
        }
      })

      this.syncFiltersUsed()
      this.$emit('update:paramsObject', params)
    }, 750),

    syncFiltersCounts () {
      this.filtersProxy.forEach(filter => {
        const currentFilterCountData = this.filtersCounts[filter.identifier]

        if (!currentFilterCountData || !filter.items) return

        filter.items.forEach(item => {
          // ESlint doesn't like `==` but we need it here
          // eslint-disable-next-line
          let found = currentFilterCountData.find(itemCountData => itemCountData.value == item.id)
          const count = found && found.count

          this.$set(item, 'count', count)
        })
      })
    },

    syncFiltersUsed () {
      this.filtersProxy.forEach(filter => {
        let isUsed

        if (filter.type === 'keyword') isUsed = Boolean(filter.value)
        else if (filter.type === 'text') isUsed = filter.items && filter.items.length
        else if (filter.type === 'proximity') isUsed = Boolean(filter.miles && filter.zip_code)
        else isUsed = filter.items && filter.items.some(item => item.selected)

        this.$set(filter, 'isUsed', isUsed)
      })
    },

    reset () {
      this.filtersProxy = cloneDeep(this.filters)
      this.genParamsObject()
    },

    resetErrorState (item) {
      this.$set(item, 'error', null)
    },

    addCustomText (filter) {
      if (!filter.value) {
        this.$set(filter, 'error', 'A value is required!')
        return
      }

      if (filter.value.length > 50) {
        this.$set(filter, 'error', 'Keywords must be less than 50 characters')
        return
      }

      const customItem = {
        id: `${filter.value}|null`,
        description: filter.value
      }

      if (filter.items && filter.items.find(item => item.id === customItem.id)) {
        this.$set(filter, 'error', 'Entry already exists!')
        return
      }

      if (!filter.items) this.$set(filter, 'items', [])

      filter.items.push(customItem)
      filter.value = null

      this.genParamsObject()
    },

    removeCustomText (items, itemToRemove) {
      items.splice(items.findIndex(item => item.id === itemToRemove.id), 1)

      this.genParamsObject()
    },

    addCustomNumberRange (filter) {
      if ((!filter.from && filter.from !== 0) || (!filter.to && filter.to !== 0)) {
        this.$set(filter, 'error', 'Please fill out all the fields!')
        return
      }

      if (filter.from > filter.to) {
        this.$set(filter, 'error', 'Please enter a valid range!')
        return
      }

      const suffix = filter.suffix || ''
      const prefix = filter.prefix || ''
      const customItem = {
        id: `${filter.from}|${filter.to}`,
        description: `${prefix}${filter.from}${suffix} - ${prefix}${filter.to}${suffix}`,
        selected: true,
        custom: true
      }

      if (filter.items && filter.items.find(item => item.id === customItem.id)) {
        this.$set(filter, 'error', 'Entry already exists!')
        return
      }

      if (!filter.items) this.$set(filter, 'items', [])

      filter.items.push(customItem)
      filter.from = null
      filter.to = null

      this.genParamsObject()
    },
    updateMcConcentration (filter) {
      filter.value = `${filter.market_cap_range}|${filter.from}|${filter.to}`
      this.genParamsObject()
    },
    updateDynamicNumberRange (filter, item) {
      if ((!item.from && item.from !== 0) || (!item.to && item.to !== 0)) {
        this.$set(item, 'error', 'Please fill out all the fields!')
        return
      }

      if (item.from > item.to) {
        this.$set(item, 'error', 'Please enter a valid range!')
        return
      }

      item.value = `${item.id}|${item.from}|${item.to}`

      this.genParamsObject()
    },

    resetDynamicNumberRange (item) {
      this.$set(item, 'error', null)
      this.$set(item, 'from', null)
      this.$set(item, 'to', null)

      this.genParamsObject()
    },

    addQuarterRange (filter) {
      if (!filter.from || !filter.to) {
        this.$set(filter, 'error', 'Please fill out all the fields!')
        return
      }

      if (filter.from.id > filter.to.id) {
        this.$set(filter, 'error', 'Please enter a valid range!')
        return
      }

      const quarterRange = {
        id: `${filter.from.id}|${filter.to.id}`,
        description: `${filter.from.description} - ${filter.to.description}`,
        selected: true
      }

      if (filter.items && filter.items.find(item => item.id === quarterRange.id)) {
        this.$set(filter, 'error', 'Entry already exists!')
        return
      }

      if (!filter.items) this.$set(filter, 'items', [])

      filter.items.push(quarterRange)
      filter.from = null
      filter.to = null

      this.genParamsObject()
    },

    updateProximity (filter) {
      if (!filter.postalCode || !filter.miles) {
        this.$set(filter, 'error', 'Please fill out all the fields!')
        return
      }

      this.genParamsObject()
    },

    clearProximity (filter) {
      if (filter.postalCode || filter.miles) {
        delete filter.postalCode
        delete filter.miles

        this.genParamsObject()
      }

      this.$set(filter, 'error', null)
    },

    addCustomDateRange (filter) {
      if (!filter.from || !filter.to) {
        this.$set(filter, 'error', 'Please fill out all the fields!')
        return
      }

      const from = (new Date(filter.from)).getTime()
      const to = (new Date(filter.to)).getTime()

      if (from > to) {
        this.$set(filter, 'error', 'Please enter a valid range!')
        return
      }

      const customItem = {
        id: `${filter.from}|${filter.to}`,
        description: `${filter.from} - ${filter.to}`,
        selected: true,
        custom: true
      }

      if (filter.items && filter.items.find(item => item.id === customItem.id)) {
        this.$set(filter, 'error', 'Entry already exists!')
        return
      }

      if (!filter.items) this.$set(filter, 'items', [])

      filter.items.push(customItem)
      filter.from = null
      filter.to = null

      this.genParamsObject()
    }
  }
}
</script>

<style lang="scss" scoped>
::v-deep .v-expansion-panel {
  .v-expansion-panel-header {
    padding: 12px 20px;
  }
  .v-expansion-panel-content__wrap {
    padding: 16px;
  }
}
</style>
